import React, { useEffect, useState } from 'react';
import { ShieldAlert, ServerCrash, Clock, RefreshCw, Layers } from 'lucide-react';
import config from '../config';

export default function AdminSupportView() {
  const [metrics, setMetrics] = useState(null);
  const [stuck, setStuck] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mRes, sRes, fRes] = await Promise.all([
        fetch(`${config.API_URL}/internal/metrics/overview`),
        fetch(`${config.API_URL}/internal/proofs/stuck`),
        fetch(`${config.API_URL}/internal/anchors/failures`)
      ]);
      
      if (mRes.ok) setMetrics(await mRes.json());
      if (sRes.ok) {
        const d = await sRes.json();
        setStuck(d.stuck_proofs || []);
      }
      if (fRes.ok) {
        const d = await fRes.json();
        setFailures(d.failures || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-gray-100 font-sans p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <header className="flex justify-between items-center bg-[#162032] p-4 rounded-xl border border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-900/30 rounded-lg flex items-center justify-center border border-indigo-500/30">
              <ShieldAlert className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-indigo-50">Support Opérationnel (Interne)</h1>
              <p className="text-sm text-gray-400">Vue de contrôle globale du système d'ancrage</p>
            </div>
          </div>
          <button 
            onClick={fetchData}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors text-gray-300"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </header>

        {/* Global Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard title="Total Preuves" value={metrics?.total_proofs} icon={<Layers className="w-5 h-5 text-gray-500" />} />
          <MetricCard title="Ancrages Pending" value={metrics?.pending_count} valueColor="text-yellow-500" />
          <MetricCard title="Ancrages Confirmed" value={metrics?.confirmed_count} valueColor="text-green-500" />
          <MetricCard title="Ancrages Failed" value={metrics?.failed_count} valueColor="text-red-500" />
          <MetricCard title="Stuck (>15m)" value={metrics?.stuck_count} valueColor="text-orange-500" icon={<Clock className="w-5 h-5 text-orange-500" />} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stuck Proofs */}
          <div className="bg-[#162032] rounded-xl border border-gray-800 overflow-hidden flex flex-col h-[500px]">
             <div className="p-4 border-b border-gray-800 bg-[#1a2639] flex items-center gap-2">
               <Clock className="w-5 h-5 text-orange-400" />
               <h2 className="text-lg font-medium text-gray-200">Preuves "Stuck" (PENDING &gt; 15m)</h2>
             </div>
             <div className="overflow-auto p-0">
               <table className="w-full text-left text-sm">
                 <thead className="bg-[#0b1120]/80 text-gray-500 sticky top-0">
                   <tr>
                     <th className="px-4 py-3">Proof ID</th>
                     <th className="px-4 py-3">Client</th>
                     <th className="px-4 py-3">Age (min)</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-800/50">
                   {stuck.length === 0 ? (
                     <tr><td colSpan="3" className="p-4 text-center text-gray-500">Aucune preuve bloquée.</td></tr>
                   ) : (
                     stuck.map((s, i) => (
                       <tr key={i} className="hover:bg-[#1c293f]">
                         <td className="px-4 py-3 font-mono text-xs">{s.proof_id}</td>
                         <td className="px-4 py-3">{s.client_id}</td>
                         <td className="px-4 py-3 font-bold text-orange-400">{s.age_minutes}m</td>
                       </tr>
                     ))
                   )}
                 </tbody>
               </table>
             </div>
          </div>

          {/* Failures */}
          <div className="bg-[#162032] rounded-xl border border-gray-800 overflow-hidden flex flex-col h-[500px]">
             <div className="p-4 border-b border-gray-800 bg-[#1a2639] flex items-center gap-2">
               <ServerCrash className="w-5 h-5 text-red-400" />
               <h2 className="text-lg font-medium text-gray-200">Derniers échecs d'ancrage</h2>
             </div>
             <div className="overflow-auto p-0">
               <table className="w-full text-left text-sm">
                 <thead className="bg-[#0b1120]/80 text-gray-500 sticky top-0">
                   <tr>
                     <th className="px-4 py-3">Proof ID</th>
                     <th className="px-4 py-3">Message</th>
                     <th className="px-4 py-3">Date</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-800/50">
                   {failures.length === 0 ? (
                     <tr><td colSpan="3" className="p-4 text-center text-gray-500">Aucun échec récent.</td></tr>
                   ) : (
                     failures.map((f, i) => (
                       <tr key={i} className="hover:bg-[#1c293f]">
                         <td className="px-4 py-3 font-mono text-xs">{f.proof_id}</td>
                         <td className="px-4 py-3 text-red-400">{f.message}</td>
                         <td className="px-4 py-3 text-gray-400 text-xs">{new Date(f.created_at).toLocaleString()}</td>
                       </tr>
                     ))
                   )}
                 </tbody>
               </table>
             </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function MetricCard({ title, value, valueColor = "text-white", icon }) {
  return (
    <div className="bg-[#162032] p-4 rounded-xl border border-gray-800 flex flex-col justify-between">
       <div className="flex justify-between items-start">
         <span className="text-xs text-gray-400 uppercase tracking-wider">{title}</span>
         {icon}
       </div>
       <div className={`text-2xl font-bold mt-2 ${valueColor}`}>{value !== undefined ? value : '-'}</div>
    </div>
  );
}
